import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from '../../hooks/useAuthState';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useNotifications } from '../../context/NotificationContext';
import { NOTIFICATION_FLAGS } from '../../utils/featureFlags';
import { UserNotification } from '../../utils/notificationService';
import { analyticsService } from '../../utils/analyticsService';
import { IoCloseOutline, IoNotificationsOutline, IoCheckmarkCircleOutline, IoWarningOutline, IoChevronDownOutline, IoChevronUpOutline, IoChevronBackOutline, IoChevronForwardOutline } from 'react-icons/io5';
import { createLogger } from '../../utils/logger';
import { handleNavigation } from '../../utils/navigationUtils';
import { useNotificationAnalytics } from '../../hooks/useNotificationAnalytics';
import { useAdminSettings } from '../../context/AdminSettingsContext';

const logger = createLogger({ module: 'DatabaseBanner' });

interface DatabaseBannerProps {
  className?: string;
  placement?: 'home-desktop' | 'home-mobile' | 'page-top';
  onOpenXPModal?: () => void;
}

export const DatabaseBanner: React.FC<DatabaseBannerProps> = ({ 
  className = '', 
  placement = 'page-top',
  onOpenXPModal
}) => {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const infrastructureFlag = useFeatureFlag(NOTIFICATION_FLAGS.INFRASTRUCTURE_ENABLED);
  const bannerFlag = useFeatureFlag(NOTIFICATION_FLAGS.BANNER_NOTIFICATIONS_ENABLED);
  const { bannerNotifications, markAsRead, dismissNotification, isLoading, error } = useNotifications();
  const analytics = useNotificationAnalytics();
  const adminSettings = useAdminSettings();
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDebugExpanded, setIsDebugExpanded] = useState(false);
  
  // Smart stack state
  const [currentNotificationIndex, setCurrentNotificationIndex] = useState(0);

  // Consolidate feature flag checks
  const isInfrastructureEnabled = infrastructureFlag.isEnabled;
  const isBannerEnabled = bannerFlag.isEnabled;
  const isFeatureFlagsLoading = infrastructureFlag.isLoading || bannerFlag.isLoading;
  const featureFlagError = infrastructureFlag.error || bannerFlag.error;

  // Early return conditions - don't render anything if basic requirements aren't met
  const shouldRender = user?.id && isInfrastructureEnabled && isBannerEnabled && !isFeatureFlagsLoading;

  // Filter notifications to only show undismissed ones
  const activeNotifications = bannerNotifications.filter(n => !n.dismissed_at);

  // Check if user is an admin
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          const isUserAdmin = await analyticsService.isAnalyticsAdmin(user.id);
          setIsAdmin(isUserAdmin);
        } catch (error) {
          logger.error('Failed to check admin status:', error);
        }
      }
    };
    
    checkAdminStatus();
  }, [user]);

  // Reset current index when notifications change
  useEffect(() => {
    if (activeNotifications.length > 0 && currentNotificationIndex >= activeNotifications.length) {
      setCurrentNotificationIndex(0);
    }
  }, [activeNotifications.length, currentNotificationIndex]);
  
  // Track when banner notification is viewed
  // Disabled temporarily to fix infinite loop issue
  // TODO: Re-enable with proper dependency management
  /*
  useEffect(() => {
    if (!shouldRender) return;
    
    const currentNotification = activeNotifications[currentNotificationIndex];
    if (!currentNotification) return;
    
    // Track view after a delay to ensure it's actually being viewed
    const timeoutId = setTimeout(() => {
      analytics.trackNotificationViewed({
        notification_id: currentNotification.id,
        notification_type: currentNotification.notification_type,
        delivery_method: 'banner',
        category: currentNotification.category,
        template_key: currentNotification.template?.key,
        priority: currentNotification.priority,
        interaction_context: 'banner',
        target_page: currentNotification.metadata?.target_page
      });
    }, 1000); // 1 second delay to ensure user actually sees it
    
    return () => clearTimeout(timeoutId);
  }, [currentNotificationIndex, activeNotifications, shouldRender, analytics]); // Track when notification changes
  */

  // Smart stack navigation functions
  const goToNext = async () => {
    if (activeNotifications.length > 1) {
      // Mark current notification as read when navigating away
      const currentNotification = activeNotifications[currentNotificationIndex];
      if (currentNotification && !currentNotification.read_at) {
        try {
          await markAsRead(currentNotification.id);
          logger.debug('Marked notification as read during carousel navigation', {
            notification_id: currentNotification.id,
            action: 'next'
          });
        } catch (error) {
          logger.error('Failed to mark notification as read during navigation:', error);
        }
      }
      
      // Track carousel navigation
      analytics.trackBannerCarouselNavigated({
        direction: 'next',
        carousel_position: (currentNotificationIndex + 1) % activeNotifications.length,
        total_banners: activeNotifications.length,
        unread_count: activeNotifications.filter(n => !n.read_at).length
      });
      
      setCurrentNotificationIndex((prev) => (prev + 1) % activeNotifications.length);
    }
  };

  const goToPrevious = async () => {
    if (activeNotifications.length > 1) {
      // Mark current notification as read when navigating away
      const currentNotification = activeNotifications[currentNotificationIndex];
      if (currentNotification && !currentNotification.read_at) {
        try {
          await markAsRead(currentNotification.id);
          logger.debug('Marked notification as read during carousel navigation', {
            notification_id: currentNotification.id,
            action: 'previous'
          });
        } catch (error) {
          logger.error('Failed to mark notification as read during navigation:', error);
        }
      }
      
      // Track carousel navigation
      analytics.trackBannerCarouselNavigated({
        direction: 'previous',
        carousel_position: (currentNotificationIndex - 1 + activeNotifications.length) % activeNotifications.length,
        total_banners: activeNotifications.length,
        unread_count: activeNotifications.filter(n => !n.read_at).length
      });
      
      setCurrentNotificationIndex((prev) => (prev - 1 + activeNotifications.length) % activeNotifications.length);
    }
  };

  const goToIndex = async (index: number) => {
    if (index >= 0 && index < activeNotifications.length) {
      // Mark current notification as read when navigating away
      const currentNotification = activeNotifications[currentNotificationIndex];
      if (currentNotification && !currentNotification.read_at && index !== currentNotificationIndex) {
        try {
          await markAsRead(currentNotification.id);
          logger.debug('Marked notification as read during carousel navigation', {
            notification_id: currentNotification.id,
            action: 'index_select',
            target_index: index
          });
        } catch (error) {
          logger.error('Failed to mark notification as read during navigation:', error);
        }
      }
      
      setCurrentNotificationIndex(index);
    }
  };

  // Process template variables in notification content
  const processNotificationTemplate = (template: string, variables: Record<string, any>): string => {
    let processed = template;
    
    Object.entries(variables).forEach(([key, value]) => {
      const placeholder = `{{${key}}}`;
      processed = processed.replace(new RegExp(placeholder, 'g'), String(value));
    });

    return processed;
  };

  // Get template variables for the current user and notification context
  const getTemplateVariables = (notification?: UserNotification) => {
    if (!user) return {};
    
    const baseVariables = {
      user_name: user.user_metadata?.first_name || user.user_metadata?.full_name || user.email?.split('@')[0] || 'there',
      first_name: user.user_metadata?.first_name || user.email?.split('@')[0] || 'there',
      email: user.email || ''
    };

    // If notification has metadata, merge it with base variables
    if (notification?.metadata) {
      // For notifications created from events, we need to fetch the original event data
      // For now, we'll use a workaround to extract common template variables from the notification content
      const extractedVariables: Record<string, any> = {};
      
      // Try to extract note_title from the notification message if it contains the pattern
      if (notification.message && notification.metadata.template_key === 'first_note_created') {
        // Look for patterns like "You created your first note: "Title""
        const noteMatch = notification.message.match(/You created your first note: "([^"]+)"/);
        if (noteMatch) {
          extractedVariables.note_title = noteMatch[1];
        } else {
          // If the message still contains {{note_title}}, it means the database trigger didn't process it
          // This could happen for notifications created before the trigger was updated
          if (notification.message.includes('{{note_title}}')) {
            // For existing notifications, we can't recover the original note title
            // So we'll use a generic fallback
            extractedVariables.note_title = 'your note';
          }
        }
      }
      
      // Handle other common template variables that might not have been processed
      if (notification.message.includes('{{user_name}}') || notification.title.includes('{{user_name}}')) {
        // These should be handled by the base variables, but ensure they're available
        extractedVariables.user_name = baseVariables.user_name;
      }
      
      if (notification.message.includes('{{first_name}}') || notification.title.includes('{{first_name}}')) {
        extractedVariables.first_name = baseVariables.first_name;
      }
      
      return {
        ...baseVariables,
        ...notification.metadata,
        ...extractedVariables
      };
    }

    return baseVariables;
  };

  const handleNotificationClick = async (notification: UserNotification) => {
    if (!user?.id) return;

    try {
      // Mark as read
      await markAsRead(notification.id);
      
      // Track notification click
      const notificationAge = notification.created_at ? 
        Date.now() - new Date(notification.created_at).getTime() : undefined;
      
      analytics.trackNotificationClicked({
        notification_id: notification.id,
        notification_type: notification.notification_type,
        delivery_method: 'banner',
        category: notification.category,
        template_key: notification.template?.key,
        priority: notification.priority,
        time_to_interaction_ms: notificationAge,
        notification_age_ms: notificationAge,
        interaction_context: 'banner',
        resulted_in_navigation: !!notification.action_url,
        navigation_target: notification.action_url
      });

      // Log analytics (simplified for now)
      logger.info('Database notification clicked', {
        notification_id: notification.id,
        category: notification.category,
        placement
      });

      // Handle action URL
      if (notification.action_url) {
        const processedUrl = processNotificationTemplate(notification.action_url, getTemplateVariables(notification));
        
        // Handle navigation using the utility function
        handleNavigation(processedUrl, navigate, (modalType) => {
          switch (modalType) {
            case 'xp':
              if (onOpenXPModal) {
                onOpenXPModal();
              } else {
                logger.warn('XP modal handler not provided');
              }
              break;
            default:
              logger.warn('Unknown modal type:', modalType);
          }
        });
      }
    } catch (err) {
      logger.error('Error handling notification click:', err);
    }
  };

  const handleDismiss = async (notification: UserNotification) => {
    if (!user?.id) return;

    try {
      await dismissNotification(notification.id);
      
      // Log analytics (simplified for now)
      logger.info('Database notification dismissed', {
        notification_id: notification.id,
        category: notification.category,
        placement
      });
      
      // Adjust current index if needed after dismissal
      if (currentNotificationIndex >= activeNotifications.length - 1 && activeNotifications.length > 1) {
        setCurrentNotificationIndex(Math.max(0, activeNotifications.length - 2));
      }
    } catch (err) {
      logger.error('Error dismissing notification:', err);
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <IoCheckmarkCircleOutline className="w-5 h-5" />;
      case 'warning':
        return <IoWarningOutline className="w-5 h-5" />;
      case 'error':
        return <IoWarningOutline className="w-5 h-5" />;
      default:
        return <IoNotificationsOutline className="w-5 h-5" />;
    }
  };

  const getNotificationStyles = (type: string) => {
    switch (type) {
      case 'success':
        return {
          container: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
          icon: 'text-green-600 dark:text-green-400',
          title: 'text-green-800 dark:text-green-200',
          message: 'text-green-700 dark:text-green-300',
          timestamp: 'text-green-600 dark:text-green-400',
          button: 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-800/50'
        };
      case 'warning':
        return {
          container: 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800',
          icon: 'text-yellow-600 dark:text-yellow-400',
          title: 'text-yellow-800 dark:text-yellow-200',
          message: 'text-yellow-700 dark:text-yellow-300',
          timestamp: 'text-yellow-600 dark:text-yellow-400',
          button: 'text-yellow-600 dark:text-yellow-400 hover:bg-yellow-100 dark:hover:bg-yellow-800/50'
        };
      case 'error':
        return {
          container: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
          icon: 'text-red-600 dark:text-red-400',
          title: 'text-red-800 dark:text-red-200',
          message: 'text-red-700 dark:text-red-300',
          timestamp: 'text-red-600 dark:text-red-400',
          button: 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-800/50'
        };
      default: // info
        return {
          container: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
          icon: 'text-blue-600 dark:text-blue-400',
          title: 'text-blue-800 dark:text-blue-200',
          message: 'text-blue-700 dark:text-blue-300',
          timestamp: 'text-blue-600 dark:text-blue-400',
          button: 'text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-800/50'
        };
    }
  };

  // Don't render anything if basic requirements aren't met
  if (!shouldRender) {
    return null;
  }

  // Show error state for feature flag errors
  if (featureFlagError) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-3 p-4 rounded-lg border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <IoWarningOutline className="w-5 h-5 text-red-600 dark:text-red-400" />
          <div className="flex-1">
            <p className="text-sm text-red-800 dark:text-red-200">
              Feature flag error: {featureFlagError.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state only if we're actively loading notifications
  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="flex items-center gap-3 p-4 rounded-lg border bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <div className="w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded"></div>
          <div className="flex-1">
            <div className="w-32 h-4 bg-gray-300 dark:bg-gray-600 rounded mb-2"></div>
            <div className="w-48 h-3 bg-gray-300 dark:bg-gray-600 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state for notification fetch errors
  if (error) {
    return (
      <div className={`${className}`}>
        <div className="flex items-center gap-3 p-4 rounded-lg border bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <IoWarningOutline className="w-5 h-5 text-red-600 dark:text-red-400" />
          <div className="flex-1">
            <p className="text-sm text-red-800 dark:text-red-200">
              {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Don't render if no notifications (but don't show loading anymore)
  if (activeNotifications.length === 0) {
    return null;
  }

  // Get current notification
  const currentNotification = activeNotifications[currentNotificationIndex];
  const hasMultipleNotifications = activeNotifications.length > 1;

  if (!currentNotification) {
    return null;
  }

  const styles = getNotificationStyles(currentNotification.notification_type);

  return (
    <div className={`${className}`}>
      {/* Smart Stack Container */}
      <div className="relative">
        {/* Main notification */}
        <div className={`flex items-start gap-3 p-4 rounded-lg border shadow-sm transition-all duration-300 ${styles.container}`}>
          {/* Left navigation arrow - centered vertically */}
          {hasMultipleNotifications && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await goToPrevious();
              }}
              className={`shrink-0 p-1 rounded-md ${styles.button} opacity-70 hover:opacity-100 transition-opacity self-center`}
              aria-label="Previous notification"
            >
              <IoChevronBackOutline className="w-4 h-4" />
            </button>
          )}

          <div className={`shrink-0 mt-0.5 ${styles.icon}`}>
            {getNotificationIcon(currentNotification.notification_type)}
          </div>
          
          <div 
            className="flex-1 cursor-pointer relative"
            onClick={() => handleNotificationClick(currentNotification)}
          >
            <h4 className={`font-medium text-sm ${styles.title}`}>
              {processNotificationTemplate(currentNotification.title, getTemplateVariables(currentNotification))}
            </h4>
            <p className={`text-sm mt-1 ${styles.message}`}>
              {processNotificationTemplate(currentNotification.message, getTemplateVariables(currentNotification))}
            </p>
            {currentNotification.action_text && (
              <button className={`text-sm mt-2 font-medium underline ${styles.title}`}>
                {processNotificationTemplate(currentNotification.action_text, getTemplateVariables(currentNotification))}
              </button>
            )}
            <p className={`text-xs mt-2 opacity-75 ${styles.timestamp}`}>
              {new Date(currentNotification.created_at).toLocaleTimeString()}
            </p>
            
            {/* Banner counter in lower right corner */}
            {hasMultipleNotifications && (
              <div className={`absolute bottom-0 right-0 text-xs px-2 py-1 rounded-tl-md ${styles.container} border-l border-t opacity-75`}>
                {currentNotificationIndex + 1}/{activeNotifications.length}
              </div>
            )}
          </div>

          {/* Right navigation arrow - centered vertically */}
          {hasMultipleNotifications && (
            <button
              onClick={async (e) => {
                e.stopPropagation();
                await goToNext();
              }}
              className={`shrink-0 p-1 rounded-md ${styles.button} opacity-70 hover:opacity-100 transition-opacity self-center`}
              aria-label="Next notification"
            >
              <IoChevronForwardOutline className="w-4 h-4" />
            </button>
          )}
          
          {/* Dismiss button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDismiss(currentNotification);
            }}
            className={`shrink-0 p-1 rounded-md ${styles.button}`}
            aria-label="Dismiss notification"
          >
            <IoCloseOutline className="w-4 h-4" />
          </button>
        </div>

        {/* Dot indicators */}
        {hasMultipleNotifications && (
          <div className="flex justify-center mt-2 gap-1">
            {activeNotifications.map((_, index) => (
              <button
                key={index}
                onClick={async () => await goToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${
                  index === currentNotificationIndex
                    ? `${styles.icon.replace('text-', 'bg-').replace('dark:text-', 'dark:bg-')} opacity-100`
                    : 'bg-gray-300 dark:bg-gray-600 opacity-50 hover:opacity-75'
                }`}
                aria-label={`Go to notification ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
      
      {/* Debug info for admin users only */}
      {isAdmin && adminSettings.settings.showDatabaseBanner && (
        <div className="mt-4 p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg text-xs">
          <button
            onClick={() => setIsDebugExpanded(!isDebugExpanded)}
            className="flex items-center gap-2 w-full text-left font-medium text-purple-700 dark:text-purple-300 mb-2 hover:text-purple-800 dark:hover:text-purple-200"
          >
            {isDebugExpanded ? (
              <IoChevronUpOutline className="w-4 h-4" />
            ) : (
              <IoChevronDownOutline className="w-4 h-4" />
            )}
            Database Banner Debug (Admin Only)
          </button>
          
          {isDebugExpanded && (
            <>
              <div className="mb-2 p-2 bg-green-100 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                <p className="text-green-700 dark:text-green-300 font-medium">✅ V2 Realtime Integration</p>
                <p className="text-green-600 dark:text-green-400 text-xs mt-1">
                  Using NotificationContext with real-time subscriptions enabled.
                </p>
              </div>
              <ul className="space-y-1 text-purple-600 dark:text-purple-400">
                <li>Active Notifications: {activeNotifications.length}</li>
                <li>Total Banner Notifications: {bannerNotifications.length}</li>
                <li>Current index: {currentNotificationIndex + 1}/{activeNotifications.length}</li>
                <li>User ID: {user?.id}</li>
                <li>Placement: {placement}</li>
                <li>Infrastructure Enabled: {isInfrastructureEnabled ? '✅' : '❌'}</li>
                <li>Banner Notifications: {isBannerEnabled ? '✅' : '❌'}</li>
                <li>Feature Flags Loading: {isFeatureFlagsLoading ? '⏳' : '✅'}</li>
                <li>Context Loading: {isLoading ? '⏳' : '✅'}</li>
                <li>Admin Status: {isAdmin ? '✅' : '❌'}</li>
                <li>Realtime Status: ✅ Connected via NotificationContext</li>
                <li>Template Variables: {JSON.stringify(getTemplateVariables())}</li>
                {activeNotifications.length > 0 && (
                  <li>Current Notification: 
                    <div className="ml-2 mt-1 text-xs">
                      <div>Raw Title: {currentNotification.title}</div>
                      <div>Processed Title: {processNotificationTemplate(currentNotification.title, getTemplateVariables(currentNotification))}</div>
                      <div>Raw Message: {currentNotification.message}</div>
                      <div>Processed Message: {processNotificationTemplate(currentNotification.message, getTemplateVariables(currentNotification))}</div>
                      <div>Notification Metadata: {JSON.stringify(currentNotification.metadata)}</div>
                      <div>Combined Variables: {JSON.stringify(getTemplateVariables(currentNotification))}</div>
                    </div>
                  </li>
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}; 